import { useState, useEffect, useCallback, useMemo } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Search, Pencil, Trash2, Package, Upload, X, Filter } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// ===== CATEGORY TREE =====
const CATEGORIES = {
  Costumes: [],
  "Donated Garments": [],
  Accessories: {
    "Jewellery": ["Necklaces", "Earrings", "Bracelets", "Rings", "Anklets", "Brooches", "Hair Jewellery"],
    "Headwear": ["Hats", "Headbands", "Fascinators", "Veils", "Hair Clips", "Hair Pins"],
    "Hand & Arm": ["Gloves (Short)", "Gloves (Long)", "Gloves (Lace)", "Gloves (Leather)", "Arm Cuffs", "Wristbands"],
    "Face & Eye": ["Glasses", "Sunglasses", "Masks"],
    "Neck & Shoulders": ["Scarves", "Shawls", "Capes", "Collars"],
    "Waist & Body": ["Belts", "Harnesses", "Sashes"],
    "Bags": ["Handbags", "Clutches", "Backpacks", "Costume Bags"],
    "Footwear": ["Heels", "Flats", "Boots", "Sandals"],
    "Other Accessories": ["Pins", "Decorative Pieces", "Costume Props"],
  },
  Machinery: {
    "Domestic": ["Domestic Straight Stitch", "Domestic Overlock"],
    "Industrial": ["Industrial Straight Stitch", "Industrial Overlock", "Industrial Coverstitch"],
    "Pressing Equipment": ["Irons", "Industrial Boilers", "Ironing Boards", "Steamers"],
    "Special Equipment": ["Heat Press", "Cutting Machines"],
    "Filming Equipment": ["Lavalier Microphones", "Cameras", "Tripods", "Lighting Equipment", "Phone Flip Screens", "TVs / Displays"],
  },
  Haberdashery: {
    "Threads": ["Polyester Thread", "Cotton Thread", "Silk Thread", "Overlock Thread"],
    "Beads": ["Pearls", "Crystals", "Glass Beads", "Plastic Beads", "Metal Beads", "Decorative Beads"],
    "Fastenings": ["Buttons", "Hooks and Eyes", "Magnetic Snaps", "Press Studs", "Velcro"],
    "Structural": ["Boning", "Interfacing", "Padding"],
    "Trims": ["Lace Trims", "Ribbon Trims", "Decorative Trims", "Fringing"],
    "Construction": ["Elastic", "Bias Binding", "Zippers"],
    "Decorative": ["Brooches", "Appliques", "Embellishments"],
    "Tools & Supplies": ["Glue", "Fabric Glue", "Adhesives", "Sewing Tools"],
  },
  Fabrics: [],
};

const CATEGORY_NAMES = Object.keys(CATEGORIES);
const CONDITIONS = ["new", "used", "donated", "damaged"];
const FABRIC_TYPES = ["Cotton", "Silk", "Chiffon", "Denim", "Linen", "Polyester", "Satin", "Velvet", "Wool", "Organza", "Tulle", "Jersey", "Lace", "Leather", "Other"];
const PATTERNS = ["Plain", "Floral", "Striped", "Checked", "Polka Dot", "Abstract", "Geometric", "Other"];
const WEIGHTS = ["Light", "Medium", "Heavy"];

function getSubcategories(category) {
  const subs = CATEGORIES[category];
  if (!subs || Array.isArray(subs)) return [];
  const flat = [];
  Object.entries(subs).forEach(([group, items]) => {
    items.forEach((item) => flat.push({ group, label: item }));
  });
  return flat;
}

function getSubcategoryGroups(category) {
  const subs = CATEGORIES[category];
  if (!subs || Array.isArray(subs)) return {};
  return subs;
}

export default function AdminInventory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterSubcategory, setFilterSubcategory] = useState("all");
  const [filterCondition, setFilterCondition] = useState("all");
  const [filterColor, setFilterColor] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "", category: "Costumes", subcategory: "", quantity: 1, description: "",
    color: "", tags: "", location: "", condition: "new", image_path: "",
    fabric_type: "", pattern: "", weight: "", stretch: false, notes: "",
  });

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filterCategory !== "all") params.category = filterCategory;
      if (filterSubcategory !== "all") params.subcategory = filterSubcategory;
      if (filterCondition !== "all") params.condition = filterCondition;
      if (filterColor) params.color = filterColor;
      if (search) params.search = search;
      const { data } = await api.get("/inventory", { params });
      setItems(data);
    } catch { setItems([]); }
    setLoading(false);
  }, [search, filterCategory, filterSubcategory, filterCondition, filterColor]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", category: filterCategory !== "all" ? filterCategory : "Costumes", subcategory: "", quantity: 1, description: "", color: "", tags: "", location: "", condition: "new", image_path: "", fabric_type: "", pattern: "", weight: "", stretch: false, notes: "" });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "", category: item.category || "Costumes", subcategory: item.subcategory || "",
      quantity: item.quantity || 0, description: item.description || "", color: item.color || "",
      tags: (item.tags || []).join(", "), location: item.location || "", condition: item.condition || "new",
      image_path: item.image_path || "", fabric_type: item.fabric_type || "", pattern: item.pattern || "",
      weight: item.weight || "", stretch: item.stretch || false, notes: item.notes || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Item name is required"); return; }
    const payload = { ...form, quantity: Number(form.quantity) || 0, tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [] };
    try {
      if (editing) { await api.put(`/inventory/${editing.id}`, payload); toast.success("Item updated"); }
      else { await api.post("/inventory", payload); toast.success("Item added"); }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try { await api.delete(`/inventory/${item.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_path: data.storage_path }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const subcats = useMemo(() => getSubcategories(form.category), [form.category]);
  const filterSubcats = useMemo(() => getSubcategories(filterCategory), [filterCategory]);

  // Stats
  const catCounts = useMemo(() => {
    const counts = {};
    items.forEach((i) => { counts[i.category] = (counts[i.category] || 0) + 1; });
    return counts;
  }, [items]);

  return (
    <AdminLayout>
      <div data-testid="admin-inventory" className="space-y-6 animate-fade-in-up">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl text-[#5C3A2E] font-semibold tracking-tight">Inventory</h1>
            <p className="text-[#2B2B2B]/50 mt-1 text-sm">{items.length} items{filterCategory !== "all" ? ` in ${filterCategory}` : ""}</p>
          </div>
          <Button data-testid="add-item-button" onClick={openCreate} className="bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 self-start sm:self-auto">
            <Plus className="w-4 h-4 mr-1.5" /> Add Item
          </Button>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => { setFilterCategory("all"); setFilterSubcategory("all"); }} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${filterCategory === "all" ? "bg-[#5C3A2E] text-white shadow-[0_2px_8px_rgb(92,58,46,0.2)]" : "bg-[#EADBC8]/30 text-[#2B2B2B]/60 hover:bg-[#EADBC8]/50"}`}>
            All
          </button>
          {CATEGORY_NAMES.map((cat) => (
            <button key={cat} data-testid={`cat-tab-${cat}`} onClick={() => { setFilterCategory(cat); setFilterSubcategory("all"); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${filterCategory === cat ? "bg-[#5C3A2E] text-white shadow-[0_2px_8px_rgb(92,58,46,0.2)]" : "bg-[#EADBC8]/30 text-[#2B2B2B]/60 hover:bg-[#EADBC8]/50"}`}>
              {cat}
            </button>
          ))}
        </div>

        {/* Filters Row */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#2B2B2B]/30" />
            <Input data-testid="inventory-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search items..." className="pl-10 rounded-xl border-[#EADBC8]" />
          </div>
          {filterSubcats.length > 0 && (
            <Select value={filterSubcategory} onValueChange={setFilterSubcategory}>
              <SelectTrigger data-testid="filter-subcategory" className="w-full sm:w-48 rounded-xl border-[#EADBC8]"><SelectValue placeholder="Subcategory" /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="all">All Subcategories</SelectItem>
                {filterSubcats.map((s) => <SelectItem key={s.label} value={s.label}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={filterCondition} onValueChange={setFilterCondition}>
            <SelectTrigger data-testid="filter-condition" className="w-full sm:w-36 rounded-xl border-[#EADBC8]"><SelectValue placeholder="Condition" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any Condition</SelectItem>
              {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="filter-color" value={filterColor} onChange={(e) => setFilterColor(e.target.value)} placeholder="Color..." className="w-full sm:w-32 rounded-xl border-[#EADBC8]" />
        </div>

        {/* Items Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3].map((i) => <div key={i} className="h-40 shimmer rounded-xl" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-12 h-12 text-[#EADBC8] mx-auto mb-3" />
            <p className="text-[#2B2B2B]/40 text-sm">No items found. Add your first inventory item.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((item) => (
              <div key={item.id} data-testid={`item-card-${item.id}`} className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 overflow-hidden studio-card group">
                {/* Image */}
                {item.image_path ? (
                  <div className="aspect-[4/3] bg-[#F7F3EF] overflow-hidden">
                    <img src={`${API_BASE}/api/files/${item.image_path}`} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                ) : (
                  <div className="aspect-[4/3] bg-[#F7F3EF] flex items-center justify-center">
                    <Package className="w-10 h-10 text-[#EADBC8]" />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-medium text-[#2B2B2B] truncate">{item.name}</h3>
                      <p className="text-xs text-[#2B2B2B]/40 mt-0.5">{item.category}{item.subcategory ? ` · ${item.subcategory}` : ""}</p>
                    </div>
                    <span className="text-lg font-semibold text-[#5C3A2E] flex-shrink-0">{item.quantity}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {item.color && <span className="px-2 py-0.5 rounded-full text-[10px] bg-[#EADBC8]/30 text-[#5C3A2E]">{item.color}</span>}
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${item.condition === "new" ? "bg-[#5C3A2E]/10 text-[#5C3A2E]" : item.condition === "damaged" ? "bg-red-100 text-red-700" : "bg-[#C6A969]/20 text-[#5C3A2E]"}`}>{item.condition}</span>
                    {item.location && <span className="text-[10px] text-[#2B2B2B]/30">{item.location}</span>}
                  </div>
                  <div className="flex items-center gap-1 mt-3 pt-3 border-t border-[#EADBC8]/20">
                    <Button data-testid={`edit-item-${item.id}`} variant="ghost" size="sm" onClick={() => openEdit(item)} className="text-[#5C3A2E] hover:bg-[#EADBC8]/30 rounded-full h-7 w-7 p-0">
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button data-testid={`delete-item-${item.id}`} variant="ghost" size="sm" onClick={() => handleDelete(item)} className="text-red-400 hover:bg-red-50 rounded-full h-7 w-7 p-0">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white rounded-xl max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#5C3A2E]">{editing ? "Edit Item" : "Add Inventory Item"}</DialogTitle>
              <DialogDescription className="text-[#2B2B2B]/50 text-sm">Fill in the item details</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              {/* Image */}
              <div>
                {form.image_path ? (
                  <div className="relative aspect-video rounded-xl overflow-hidden bg-[#F7F3EF] mb-2">
                    <img src={`${API_BASE}/api/files/${form.image_path}`} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setForm({ ...form, image_path: "" })} className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1 hover:bg-black/70"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center aspect-video rounded-xl border-2 border-dashed border-[#EADBC8] bg-[#F7F3EF] cursor-pointer hover:border-[#C6A969] transition-colors mb-2">
                    <div className="text-center">
                      <Upload className="w-6 h-6 text-[#EADBC8] mx-auto mb-1" />
                      <span className="text-xs text-[#2B2B2B]/40">Upload photo</span>
                    </div>
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                  </label>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Name</label>
                  <Input data-testid="item-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Item name" className="rounded-xl border-[#EADBC8]" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Category</label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, subcategory: "" })}>
                    <SelectTrigger data-testid="item-category" className="rounded-xl border-[#EADBC8]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORY_NAMES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Subcategory</label>
                  {subcats.length > 0 ? (
                    <Select value={form.subcategory || "none"} onValueChange={(v) => setForm({ ...form, subcategory: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="item-subcategory" className="rounded-xl border-[#EADBC8]"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        <SelectItem value="none">None</SelectItem>
                        {Object.entries(getSubcategoryGroups(form.category)).map(([group, items]) => (
                          items.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} placeholder="Optional" className="rounded-xl border-[#EADBC8]" />
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Quantity</label>
                  <Input data-testid="item-quantity" type="number" min="0" step="0.5" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="rounded-xl border-[#EADBC8]" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Condition</label>
                  <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                    <SelectTrigger data-testid="item-condition" className="rounded-xl border-[#EADBC8]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Color</label>
                  <Input data-testid="item-color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="e.g., Red, Navy Blue" className="rounded-xl border-[#EADBC8]" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Location</label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g., Shelf B3" className="rounded-xl border-[#EADBC8]" />
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Tags (comma separated)</label>
                  <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="e.g., vintage, silk, wedding" className="rounded-xl border-[#EADBC8]" />
                </div>
              </div>

              {/* Fabric-specific fields */}
              {form.category === "Fabrics" && (
                <div className="border-t border-[#EADBC8]/30 pt-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#C6A969] mb-3">Fabric Details</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Fabric Type</label>
                      <Select value={form.fabric_type || "none"} onValueChange={(v) => setForm({ ...form, fabric_type: v === "none" ? "" : v })}>
                        <SelectTrigger className="rounded-xl border-[#EADBC8]"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{FABRIC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Pattern</label>
                      <Select value={form.pattern || "none"} onValueChange={(v) => setForm({ ...form, pattern: v === "none" ? "" : v })}>
                        <SelectTrigger className="rounded-xl border-[#EADBC8]"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{PATTERNS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Weight</label>
                      <Select value={form.weight || "none"} onValueChange={(v) => setForm({ ...form, weight: v === "none" ? "" : v })}>
                        <SelectTrigger className="rounded-xl border-[#EADBC8]"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{WEIGHTS.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer pb-2">
                        <input type="checkbox" checked={form.stretch} onChange={(e) => setForm({ ...form, stretch: e.target.checked })} className="rounded border-[#EADBC8] text-[#5C3A2E] focus:ring-[#C6A969]" />
                        <span className="text-sm text-[#2B2B2B]">Has Stretch</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Description / Notes</label>
                <Textarea value={form.description || form.notes} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Optional notes..." className="rounded-xl border-[#EADBC8] resize-none" />
              </div>

              <Button data-testid="save-item-button" onClick={handleSave} className="w-full bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-11">
                {editing ? "Save Changes" : "Add Item"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
